import { Injectable, inject } from '@angular/core';
import { HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';

import { AuthenticationService } from '@core/authentication/authentication.service';
import { catchError, map, switchMap, timeout } from 'rxjs/operators';
import { of, throwError } from 'rxjs';
import { AbookSnackbarService } from '@app/shared/components/abook-snackbar/abook-snackbar.service';
import { SKIP_ERROR_SNACKBAR } from './skip-error-snackbar';
import { userFacingRejection } from './user-facing-rejection';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private abookSnackbarService = inject(AbookSnackbarService);
  private auth = inject(AuthenticationService);

  intercept(req: HttpRequest<unknown>, next: HttpHandler) {
    // Prevent assest auth check. Asset URLs are relative so they resolve under the
    // app's base href (GitHub Pages previews serve from a subdirectory) — matching
    // on a leading slash alone would miss them and attach the token to static files.
    if (req.url.startsWith('assets/') || req.url.includes('/assets/')) {
      return next.handle(req);
    }

    return this.auth.getAccessToken().pipe(
      map((token) => req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })),

      switchMap((authRequest) =>
        next.handle(authRequest).pipe(
          catchError((error) => {
            let handled = false;

            if (error instanceof HttpErrorResponse) {
              if (error.error instanceof ErrorEvent) {
                console.error('Error Event');
              } else {
                switch (error.status) {
                  case 0: // Network error (ERR_CONNECTION_REFUSED, timeout, etc.)
                    handled = true;
                    this.abookSnackbarService.showError(
                      'Netværksfejl',
                      'Kunne ikke oprette forbindelse til serveren. Tjek din internetforbindelse.',
                    );
                    break;
                  case 401: // login
                    // Straight back to the identity provider. Not via
                    // `/authentication/unauthorized`: that page means "signed in and refused",
                    // and the OIDC client can still believe this session is alive — so it would
                    // tell an administrator whose token just died that they lack a permission.
                    handled = true;
                    this.abookSnackbarService.showWarning(
                      'Session udløbet',
                      'Din login session er udløbet og du skal logge ind påny.',
                    );
                    this.auth.signIn();
                    break;
                  case 403: // forbidden
                    handled = true;
                    this.abookSnackbarService.showError(
                      'Adgang nægtet',
                      'Du har ikke adgang til at udeføre denne handling',
                    );
                    break;
                  case 400: {
                    // En 400 er en afvisning med en begrundelse — fx en booking der sprænger
                    // maksimumtiden. Kun de beskeder, API'et har markeret som skrevet til brugeren
                    // (`userFacing`), vises ordret: resten af API'ets 400'ere er engelske,
                    // tekniske og gengiver undertiden en betalingsgateways egne ord, og de hører
                    // ikke hjemme på en brugers skærm. Fejlen kastes videre, så kaldstedet stadig
                    // kan rydde op i sin egen tilstand.
                    const detail = userFacingRejection(error);
                    if (detail) {
                      handled = true;

                      // Kurven og andre, der viser begrundelsen på skærmen selv, beder om at
                      // slippe for den samme sætning i en snackbar oveni.
                      if (!req.context.get(SKIP_ERROR_SNACKBAR)) {
                        this.abookSnackbarService.showError('Handlingen blev afvist', detail);
                      }

                      return throwError(() => error);
                    }
                    break;
                  }
                }
              }
            } else {
              console.error('Other Errors');
            }

            if (handled) {
              // NOTE: the error is emitted as a *value* — subscribers see it on the success
              // channel. A call whose failure must not pass for success has to use
              // `rethrowHandledHttpError()` (same folder); several already do. The one exception is
              // the 400 branch above, which rethrows itself: a rejected write must never look like
              // a save, and every caller of it needs to clean up its own state.
              return of(error);
            } else {
              this.abookSnackbarService.showError('Fejl', 'Der opstod en fejl');
              return throwError(error);
            }
          }),

          timeout(120000),
        ),
      ),
    );
  }
}
